import type { Event, EventHint } from '@sentry/nextjs'

// Errors coming from browser/OS/extension environments we don't control and can't fix.
// These are noise, not actionable bugs - drop them before they reach Sentry.
export const SENTRY_IGNORE_ERRORS = [
  // Android WebView JS-interface teardown artifact: the injected Java object
  // is garbage-collected mid-call when the WebView is backgrounded/destroyed.
  // Native-side lifecycle issue, not something the web app can prevent.
  /Error invoking postMessage/i,

  // Meta's in-app browser (Instagram/Facebook) injects an inline script that throws when
  // its own window._META object isn't there. The app never references _META itself.
  // WebKit: "...evaluating 'window._META.IABInnerFrame'", Chromium: "...reading 'IABInnerFrame'".
  /window\._META\b|IABInnerFrame/,

  // Browser extensions injecting themselves into the page
  /Invalid call to runtime\.sendMessage/i,
  /Extension context invalidated/i,

  // Network blips outside our control (client offline, mobile network drop, proxy resets)
  /socket hang up/i,
  /Network Error/i,
  /ECONNRESET/i,
  /ECONNABORTED/i,
  /Failed to fetch/i,
  /Load failed/i,

  // Known-benign Next.js/browser quirks
  /Invariant: attempted to hard navigate to the same URL/i,
  /ResizeObserver loop limit exceeded/i,
  /Non-Error promise rejection captured/i,
]

const getAxiosStatusCode = (hint: EventHint): number | undefined => {
  const error = hint.originalException as { isAxiosError?: boolean; response?: { status?: number } }
  return error?.isAxiosError ? error.response?.status : undefined
}

// AxiosErrors for expected client-side responses (bad input, missing resource, auth)
// are not application bugs - keep them out of the error stream but still record them
// as warnings so we retain visibility without noisy alerting.
export const sentryBeforeSend = (event: Event, hint: EventHint): Event | null => {
  const status = getAxiosStatusCode(hint)

  if (status && status >= 400 && status < 500) {
    event.level = 'warning'
  }

  return event
}
